import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronLeft, ChevronRight, X, BookOpen, Home, Users, CalendarCheck, MessageCircle, Phone } from "lucide-react";
import { PARAYANA_ITEMS, PARAYANA_FAQS, PARAYANA_MEDIA, PARAYANA_VIDEOS, PARAYANA_LEADER } from "@/data/astrologyParayana";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WaitlistModal } from "./landing/WaitlistModal";
import { openBooking } from "@/lib/booking";

const HOW = [
  { Icon: BookOpen, t: "Choose the text", d: "Vishnu or Lalitha Sahasranama, Soundarya Lahari and more." },
  { Icon: Users, t: "A group of vaidikas", d: "Trained chanters recite the full text together, in your family's tradition and gotra." },
  { Icon: Home, t: "At home or temple", d: "Hosted in your home or performed at a temple in your name — you choose." },
  { Icon: CalendarCheck, t: "For any occasion", d: "Health, prosperity, protection, birthdays, house blessings, shraddha and festivals." },
];

const ParayanaCard = ({ p, openWaitlist }) => (
  <article className="flex h-full flex-col rounded-[16px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm transition-shadow hover:shadow-md" data-testid={`parayana-card-${p.slug}`}>
    <div className="flex items-start gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#B23A2B]/10 text-[#B23A2B]">
        <BookOpen size={22} strokeWidth={1.8} />
      </span>
      <div>
        <p className="font-baloo text-base text-[#C99B55]">{p.kn}</p>
        <h3 className="font-eczar text-lg font-semibold text-[#3B221B]">{p.en}</h3>
      </div>
    </div>
    <p className="mt-3 font-mukta text-sm text-[#6B5044]">{p.desc}</p>
    <p className="mt-3 font-mukta text-xs text-[#6B5044]"><span className="font-semibold text-[#3B221B]">When:</span> {p.when}</p>
    <div className="mt-4 flex flex-wrap items-center gap-4">
      {p.stotra && (
        <Link to={`/stotras/${p.stotra}`} data-testid={`parayana-read-${p.slug}`}
          className="inline-flex items-center gap-1 font-mukta text-sm font-semibold text-[#3E6B4F] hover:text-[#2c5039]">
          Read the text <ArrowRight size={14} />
        </Link>
      )}
      <button onClick={() => openWaitlist("Family")} data-testid={`parayana-cta-${p.slug}`}
        className="inline-flex items-center gap-1 font-mukta text-sm font-semibold text-[#A8722A] hover:text-[#8f5f21]">
        Enquire / early access <ArrowRight size={14} />
      </button>
    </div>
  </article>
);

const EnquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", occasion: "", date: "", message: "" });
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const submit = (e) => {
    e.preventDefault();
    const msg = `Namaste, I would like to enquire about a group parayana.\n\nName: ${form.name || "-"}\nOccasion / parayana: ${form.occasion || "-"}\nPreferred date: ${form.date || "-"}\nMessage: ${form.message || "-"}\n\n(Sent via purohithconnect.com)`;
    window.open(`https://wa.me/${PARAYANA_LEADER.phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    onClose();
  };
  const field = "mt-1 w-full rounded-lg border border-[#A8722A]/30 bg-white px-3 py-2 font-mukta text-sm text-[#3B221B] outline-none focus:border-[#A8722A] focus:ring-2 focus:ring-[#A8722A]/30";
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="parayana-enquiry-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-[18px] bg-[#FBF6EC] p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-baloo text-sm text-[#B23A2B]">ಪಾರಾಯಣ ವಿಚಾರಣೆ</p>
            <h3 className="font-eczar text-xl font-bold text-[#3B221B]">Enquire about a parayana</h3>
            <p className="mt-0.5 font-mukta text-xs text-[#6B5044]">Your details open in WhatsApp to {PARAYANA_LEADER.name} ji.</p>
          </div>
          <button onClick={onClose} data-testid="enquiry-close" aria-label="Close" className="rounded-full p-1 text-[#6B5044] hover:bg-black/5"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="block font-mukta text-sm font-medium text-[#3B221B]">Your name
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="enquiry-name" className={field} placeholder="e.g. Lakshmi" />
          </label>
          <label className="block font-mukta text-sm font-medium text-[#3B221B]">Occasion / parayana
            <input value={form.occasion} onChange={(e) => setForm((f) => ({ ...f, occasion: e.target.value }))} data-testid="enquiry-occasion" className={field} placeholder="e.g. Lalitha Sahasranama for housewarming" />
          </label>
          <label className="block font-mukta text-sm font-medium text-[#3B221B]">Preferred date
            <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="enquiry-date" className={field} />
          </label>
          <label className="block font-mukta text-sm font-medium text-[#3B221B]">Message <span className="font-normal text-[#6B5044]">(optional)</span>
            <textarea rows={3} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="enquiry-message" className={field} placeholder="Location, number of people, any details" />
          </label>
          <button type="submit" data-testid="enquiry-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#3E6B4F] px-5 py-2.5 font-mukta font-semibold text-white transition-colors hover:bg-[#2c5039]">
            <MessageCircle size={18} /> Send on WhatsApp
          </button>
        </form>
      </div>
    </div>
  );
};

const LeaderCard = ({ onEnquire }) => (
  <section className="mt-12" data-testid="parayana-leader">
    <div className="mx-auto grid max-w-4xl items-center gap-6 rounded-[20px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm md:grid-cols-[auto_1fr] md:p-8">
      <img src={PARAYANA_LEADER.image} alt={`${PARAYANA_LEADER.name}, ${PARAYANA_LEADER.title}`} loading="lazy"
        className="mx-auto h-40 w-40 rounded-full object-cover object-top shadow-md ring-4 ring-[#A8722A]/20 md:h-44 md:w-44" />
      <div>
        <p className="font-baloo text-base text-[#B23A2B]">ಪಾರಾಯಣ ಮಾರ್ಗದರ್ಶಕರು · Led by</p>
        <h2 className="mt-1 font-eczar text-2xl font-bold text-[#3B221B]" data-testid="leader-name">{PARAYANA_LEADER.name}</h2>
        <p className="font-mukta text-sm font-semibold text-[#A8722A]">{PARAYANA_LEADER.title} · {PARAYANA_LEADER.experience} experience</p>
        <p className="mt-3 font-mukta text-sm text-[#6B5044]">{PARAYANA_LEADER.bio}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={onEnquire} data-testid="leader-enquiry-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#3E6B4F] px-5 py-2.5 font-mukta text-sm font-semibold text-white transition-colors hover:bg-[#2c5039]">
            <MessageCircle size={17} /> Enquire on WhatsApp
          </button>
          <a href={`tel:+${PARAYANA_LEADER.phone}`} data-testid="leader-call-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#A8722A]/50 px-5 py-2.5 font-mukta text-sm font-semibold text-[#A8722A] transition-colors hover:bg-[#A8722A] hover:text-white">
            <Phone size={17} /> Call {PARAYANA_LEADER.phoneDisplay}
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default function ParayanaPage() {
  const [waitlist, setWaitlist] = useState({ open: false, role: "Family" });
  const openWaitlist = () => openBooking({ location: "parayana" });
  const [lightbox, setLightbox] = useState(null);
  const [enquiry, setEnquiry] = useState(false);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + PARAYANA_MEDIA.length) % PARAYANA_MEDIA.length);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % PARAYANA_MEDIA.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  useSeo({
    title: "Group Parayana in Bengaluru — ಪಾರಾಯಣ | Vishnu & Lalitha Sahasranama, Soundarya Lahari | PurohithConnect",
    description: "Book a group parayana at your home or temple in Bengaluru — Vishnu Sahasranama, Lalitha Sahasranama and Soundarya Lahari, chanted by trained vaidikas in your tradition.",
    canonicalPath: "/parayana",
    jsonLd: useMemo(() => ([
      {
        "@context": "https://schema.org", "@type": "ItemList", name: "Group parayana services",
        itemListElement: PARAYANA_ITEMS.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.en })),
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: PARAYANA_FAQS.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
      },
      {
        "@context": "https://schema.org", "@type": "ImageGallery",
        name: "Group parayana gatherings in Bengaluru",
        associatedMedia: PARAYANA_MEDIA.map((m) => ({ "@type": "ImageObject", contentUrl: m.url, caption: m.alt })),
      },
      ...PARAYANA_VIDEOS.map((v) => ({
        "@context": "https://schema.org", "@type": "VideoObject",
        name: v.title, description: v.description, contentUrl: v.url,
        thumbnailUrl: PARAYANA_MEDIA[0].url, uploadDate: "2026-09-13",
      })),
      {
        "@context": "https://schema.org", "@type": "Person",
        name: PARAYANA_LEADER.name, jobTitle: PARAYANA_LEADER.title, image: PARAYANA_LEADER.image,
        description: PARAYANA_LEADER.bio, worksFor: { "@type": "Organization", name: "PurohithConnect" },
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "Parayana", item: "https://purohithconnect.com/parayana" },
        ],
      },
    ]), []),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={openWaitlist} />
      <main className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <section className="pt-12 text-center">
          <p className="font-baloo text-base text-[#B23A2B]">ಪಾರಾಯಣ</p>
          <h1 className="mt-1 font-eczar text-4xl font-bold sm:text-5xl" data-testid="parayana-title">Group parayana, at your home or temple</h1>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
            A group of trained vaidikas chants the great sacred texts together — Sahasranamas, Soundarya Lahari and more — in your family's name, gotra and tradition. A powerful, uninterrupted flow of devotion for any occasion.
          </p>
        </section>

        <LeaderCard onEnquire={() => setEnquiry(true)} />

        {/* PARAYANA_VIDEOS — user's real parayana clips */}
        <section className="mt-12" data-testid="parayana-videos">
          <div className="text-center">
            <p className="font-baloo text-base text-[#B23A2B]">ಪಾರಾಯಣ ವೀಡಿಯೊಗಳು</p>
            <h2 className="mt-1 font-eczar text-2xl font-semibold text-[#3B221B] md:text-3xl">Watch a group parayana</h2>
            <p className="mx-auto mt-2 max-w-2xl font-mukta text-sm text-[#6B5044]">Hear the flow of a real group parayana — recorded at gatherings across Bengaluru.</p>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            {PARAYANA_VIDEOS.map((v, i) => (
              <figure key={i} data-testid={`parayana-video-${i}`} className="overflow-hidden rounded-[16px] border border-[#A8722A]/20 bg-[#FFFDF8] shadow-sm">
                <div className="aspect-video bg-black">
                  <video src={v.url} controls preload="metadata" playsInline className="h-full w-full object-contain" />
                </div>
                <figcaption className="px-4 py-3 font-mukta text-sm font-medium text-[#3B221B]">{v.title}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* PARAYANA_MEDIA gallery — user's real parayana photos (add more URLs to PARAYANA_MEDIA to grow the grid) */}
        <section className="mt-12" data-testid="parayana-gallery">
          <div className="text-center">
            <p className="font-baloo text-base text-[#B23A2B]">ಪಾರಾಯಣ ಕ್ಷಣಗಳು</p>
            <h2 className="mt-1 font-eczar text-2xl font-semibold text-[#3B221B] md:text-3xl">Moments from our parayana gatherings</h2>
            <p className="mx-auto mt-2 max-w-2xl font-mukta text-sm text-[#6B5044]">Real group parayanas hosted in temples, cultural centres and homes across Bengaluru.</p>
          </div>
          <div className="mt-6 columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
            {PARAYANA_MEDIA.map((m, i) => (
              <button key={i} onClick={() => setLightbox(i)} data-testid={`parayana-gallery-${i}`}
                className="group block w-full overflow-hidden rounded-[14px] border border-[#A8722A]/20 bg-[#FFFDF8] shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#A8722A]">
                <img src={m.url} alt={m.alt} loading="lazy"
                  className="w-full transition-transform duration-500 group-hover:scale-[1.03]" />
              </button>
            ))}
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="parayana-grid">
          {PARAYANA_ITEMS.map((p) => <ParayanaCard key={p.slug} p={p} openWaitlist={openWaitlist} />)}
        </section>

        <section className="mt-14" data-testid="parayana-how">
          <div className="text-center">
            <p className="font-baloo text-base text-[#B23A2B]">ಹೇಗೆ ನಡೆಯುತ್ತದೆ</p>
            <h2 className="mt-1 font-eczar text-2xl font-semibold text-[#3B221B] md:text-3xl">How a group parayana works</h2>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {HOW.map((h) => (
              <div key={h.t} className="rounded-[16px] border border-[#A8722A]/25 bg-[#FFFDF8] p-5 text-center" data-testid={`parayana-how-${h.t.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#B23A2B]/10 text-[#B23A2B]">
                  <h.Icon size={22} strokeWidth={1.8} />
                </span>
                <h3 className="mt-3 font-eczar text-base font-semibold text-[#3B221B]">{h.t}</h3>
                <p className="mt-1 font-mukta text-sm text-[#6B5044]">{h.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="parayana-faq-section">
          <div className="text-center">
            <p className="font-baloo text-base text-[#B23A2B]">ಪ್ರಶ್ನೋತ್ತರ</p>
            <h2 className="mt-1 font-eczar text-2xl font-semibold text-[#3B221B] md:text-3xl">Group parayana — your questions, answered</h2>
          </div>
          <div className="mx-auto mt-6 max-w-3xl space-y-3">
            {PARAYANA_FAQS.map((x, i) => (
              <details key={i} className="group rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm [&_summary::-webkit-details-marker]:hidden" data-testid={`parayana-faq-${i}`} {...(i === 0 ? { open: true } : {})}>
                <summary className="flex cursor-pointer items-center justify-between gap-3 font-eczar text-[15px] font-semibold text-[#3B221B]">
                  {x.q}
                  <ChevronDown size={18} className="shrink-0 text-[#A8722A] transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{x.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/stotras" data-testid="parayana-to-stotras" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Read stotras in the library <ArrowRight size={16} /></Link>
          <Link to="/festivals" data-testid="parayana-to-festivals" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Festival Calendar 2026 <ArrowRight size={16} /></Link>
        </div>

        <div className="mt-12 rounded-[20px] bg-[#3B221B] p-8 text-center md:p-10">
          <p className="font-eczar text-xl font-semibold text-[#FBF6EC]">Planning a parayana?</p>
          <p className="mx-auto mt-2 max-w-xl font-mukta text-sm text-[#FBF6EC]/70">Join early access and we'll arrange trained vaidikas for a home or temple parayana in your tradition.</p>
          <button onClick={() => openWaitlist("Family")} data-testid="parayana-cta-band"
            className="mt-5 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]">Get early access</button>
        </div>
      </main>
      {lightbox !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" data-testid="parayana-lightbox" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} aria-label="Close" data-testid="lightbox-close"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"><X size={24} /></button>
          <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + PARAYANA_MEDIA.length) % PARAYANA_MEDIA.length); }} aria-label="Previous" data-testid="lightbox-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 md:left-6"><ChevronLeft size={30} /></button>
          <figure className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img src={PARAYANA_MEDIA[lightbox].url} alt={PARAYANA_MEDIA[lightbox].alt} className="mx-auto max-h-[80vh] w-auto rounded-lg" />
            <figcaption className="mt-3 text-center font-mukta text-sm text-white/80">{PARAYANA_MEDIA[lightbox].alt}</figcaption>
          </figure>
          <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % PARAYANA_MEDIA.length); }} aria-label="Next" data-testid="lightbox-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 md:right-6"><ChevronRight size={30} /></button>
        </div>
      )}
      <SiteFooter />
      <EnquiryModal open={enquiry} onClose={() => setEnquiry(false)} />
      <WaitlistModal key={`${waitlist.open}-${waitlist.role}`} open={waitlist.open} defaultRole={waitlist.role}
        onClose={() => setWaitlist((w) => ({ ...w, open: false }))} />
    </div>
  );
}
